import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { handleApiError } from '../../utils/errorHandler';

const SchoolClassesTab = ({ classes: propsClasses, onClassChange }) => {
  const [classes, setClasses] = useState(propsClasses || []);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [error, setError] = useState(null);
  const [showClassForm, setShowClassForm] = useState(false);
  const [editingClassId, setEditingClassId] = useState(null);
  const [classFormData, setClassFormData] = useState({
    key: '',
    emoji: '',
    translations: [
      { language: 'en', name: '', description: '' },
    ],
  });

  const slugifyKey = (value) => (
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
  );

  // Available emoji icons for classes (unique only)
  const availableEmojis = [
    // Math
    '🔢', '📊', '📈', '📉', '➕', '➖', '✖️', '➗', '🧮', '🔟',
    // Science
    '⚛️', '🔬', '🧪', '🔭', '🌡️', '⚗️', '🧬', '🔋',
    // Reading/Writing
    '📚', '📖', '📝', '✍️', '📄', '📃', '📑', '📜', '📔', '📗',
    // History
    '🏛️', '🏺', '⚔️', '🛡️', '👑', '🗿', '⏳', '🏰', '🗡️',
    // Geography
    '🌍', '🌎', '🌏', '🗺️', '🧭', '🏔️', '🌋', '🏝️', '🏞️', '🗻',
    // Art
    '🎨', '🖼️', '🖌️', '🖍️', '✏️', '🖊️', '🖋️', '🖐️', '🎭',
    // Music
    '🎵', '🎶', '🎼', '🎹', '🥁', '🎸', '🎺', '🎻', '🎤', '🎧',
    // Sports
    '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🏓', '🏸', '🏒',
    // Technology
    '💻', '⌨️', '🖥️', '📱', '💾', '🖱️', '🖨️', '📡', '🖲️', '💿',
    // Language/Numbers
    '🔤', '🔠', '🔡', '🔣',
    // Nature
    '🌱', '🌿', '🍃', '🌾', '🌳', '🌲', '🌴', '🌵', '🌷', '🌹',
    // Stars/Space
    '⭐', '🌟', '✨', '💫', '☀️', '🌙', '🌠', '🌌', '🪐',
    // Games/Entertainment
    '🎯', '🎲', '🎮', '🃏', '🀄', '🎴', '🎰', '🎪',
    // Tools
    '📐', '📏', '📌', '📍', '📎', '✂️', '🔧', '🔨', '⚒️', '🛠️',
    // Creative
    '🧩', '🎬',
  ];

  useEffect(() => {
    if (propsClasses) {
      setClasses(propsClasses);
    } else {
      loadClasses();
    }
  }, [propsClasses]);

  const loadClasses = async () => {
    try {
      setLoadingClasses(true);
      setError(null);
      const response = await api.get('/subjects?include_inactive=false');
      const loadedClasses = response.data;
      setClasses(loadedClasses);
    } catch (err) {
      console.error('Error loading classes:', err);
      setError(handleApiError(err));
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleTranslationChange = (index, field, value) => {
    setClassFormData(prev => {
      const updated = {
        ...prev,
        translations: prev.translations.map((trans, i) => 
          i === index ? { ...trans, [field]: value } : trans
        )
      };
      
      // Name-first flow: derive internal key from first non-empty translation name while creating.
      if (!editingClassId && field === 'name') {
        const primaryName = updated.translations.find((trans) => trans.name && trans.name.trim())?.name || '';
        updated.key = slugifyKey(primaryName);
      }
      
      return updated;
    });
  };

  const addTranslation = () => {
    setClassFormData(prev => ({
      ...prev,
      translations: [...prev.translations, { language: 'en', name: '', description: '' }]
    }));
  };

  const removeTranslation = (index) => {
    setClassFormData(prev => ({
      ...prev,
      translations: prev.translations.filter((_, i) => i !== index)
    }));
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setError(null);

    const validTranslations = classFormData.translations.filter(t => t.name && t.name.trim());
    if (validTranslations.length === 0) {
      setError('At least one translation with name is required');
      return;
    }

    const fallbackName = validTranslations[0]?.name || '';
    const derivedKey = slugifyKey(classFormData.key || fallbackName) || 'subject';

    try {
      if (editingClassId) {
        await api.put(`/subjects/${editingClassId}`, {
          key: derivedKey,
          emoji: classFormData.emoji || null,
          is_active: true,
          translations: validTranslations.map(t => ({
            language: t.language,
            name: t.name.trim(),
            description: t.description?.trim() || null,
          }))
        });
      } else {
        await api.post('/subjects', {
          key: derivedKey,
          emoji: classFormData.emoji || null,
          is_active: true,
          translations: validTranslations.map(t => ({
            language: t.language,
            name: t.name.trim(),
            description: t.description?.trim() || null,
          }))
        });
      }
      
      handleCancelEdit();
      if (onClassChange) {
        await onClassChange();
        // Reload local classes after parent updates
        await loadClasses();
      } else {
        loadClasses();
      }
    } catch (err) {
      console.error('Error saving class:', err);
      setError(handleApiError(err));
    }
  };

  const handleEditClass = async (classId) => {
    try {
      setLoadingClasses(true);
      setError(null);
      const response = await api.get(`/subjects/${classId}`);
      const classData = response.data;
      
      setClassFormData({
        key: classData.key,
        emoji: classData.emoji || '',
        translations: classData.translations && classData.translations.length > 0
          ? classData.translations.map(t => ({
              language: t.language,
              name: t.name,
              description: t.description || '',
            }))
          : [
              { language: 'en', name: '', description: '' },
            ],
      });
      
      setEditingClassId(classId);
      setShowClassForm(true);
    } catch (err) {
      console.error('Error loading class for edit:', err);
      setError(handleApiError(err));
    } finally {
      setLoadingClasses(false);
    }
  };

  const handleCancelEdit = () => {
    setClassFormData({
      key: '',
      emoji: '',
      translations: [
        { language: 'en', name: '', description: '' },
      ],
    });
    setEditingClassId(null);
    setShowClassForm(false);
  };

  const handleDeleteClass = async (classId, isSystem) => {
    if (isSystem) {
      alert('System classes cannot be deleted');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this class? This action cannot be undone.')) {
      return;
    }

    try {
      await api.delete(`/subjects/${classId}`);
      if (onClassChange) {
        await onClassChange();
        // Reload local classes after parent updates
        await loadClasses();
      } else {
        loadClasses();
      }
    } catch (err) {
      console.error('Error deleting class:', err);
      setError(handleApiError(err));
    }
  };

  return (
    <div className="children-list">
      {error && (
        <div className="error-message" style={{ marginBottom: '1rem', padding: '1rem', borderRadius: '8px' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>School Classes</h2>
        <button
          className="btn-secondary"
          onClick={() => {
            setShowClassForm(true);
            setEditingClassId(null);
            setClassFormData({
              key: '',
              emoji: '',
              translations: [
                { language: 'en', name: '', description: '' },
              ],
            });
            setError(null);
          }}
        >
          Add New Class
        </button>
      </div>

      {/* Class Form Modal */}
      {showClassForm && (
        <div className="modal-overlay" onClick={handleCancelEdit}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div className="modal-header">
              <h2>{editingClassId ? 'Edit School Class' : 'Create New School Class'}</h2>
              <button
                className="modal-close-btn"
                onClick={handleCancelEdit}
                aria-label="Close modal"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleCreateClass} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>
                    Emoji Icon (optional)
                    {classFormData.emoji && (
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '1.5rem',
                        display: 'inline-block',
                        verticalAlign: 'middle'
                      }}>
                        {classFormData.emoji}
                      </span>
                    )}
                  </label>
                  <div style={{
                    border: '1px solid #cbd5e0',
                    borderRadius: '8px',
                    padding: '1rem',
                    background: '#f7fafc',
                    maxHeight: '300px',
                    overflowY: 'auto'
                  }}>
                    <div className="emoji-grid">
                      <button
                        type="button"
                        onClick={() => setClassFormData(prev => ({ ...prev, emoji: '' }))}
                        style={{
                          fontSize: '1.5rem',
                          padding: '0.75rem',
                          border: classFormData.emoji === '' ? '2px solid #667eea' : '1px solid #cbd5e0',
                          borderRadius: '8px',
                          background: classFormData.emoji === '' ? 'rgba(102, 126, 234, 0.1)' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          aspectRatio: '1',
                        }}
                        onMouseEnter={(e) => {
                          if (classFormData.emoji !== '') {
                            e.target.style.background = '#edf2f7';
                            e.target.style.borderColor = '#a0aec0';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (classFormData.emoji !== '') {
                            e.target.style.background = 'white';
                            e.target.style.borderColor = '#cbd5e0';
                          }
                        }}
                        title="None"
                      >
                        ✕
                      </button>
                      {availableEmojis.map((emoji, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => setClassFormData(prev => ({ ...prev, emoji }))}
                          style={{
                            fontSize: '1.5rem',
                            padding: '0.75rem',
                            border: classFormData.emoji === emoji ? '2px solid #667eea' : '1px solid #cbd5e0',
                            borderRadius: '8px',
                            background: classFormData.emoji === emoji ? 'rgba(102, 126, 234, 0.1)' : 'white',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            aspectRatio: '1',
                          }}
                          onMouseEnter={(e) => {
                            if (classFormData.emoji !== emoji) {
                              e.target.style.background = '#edf2f7';
                              e.target.style.borderColor = '#a0aec0';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (classFormData.emoji !== emoji) {
                              e.target.style.background = 'white';
                              e.target.style.borderColor = '#cbd5e0';
                            }
                          }}
                          title={emoji}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                  <small style={{ color: '#718096', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                    Click on an emoji icon to select it. It will appear in the class list.
                  </small>
                </div>

                <div className="form-group">
                  <label style={{ marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: 600 }}>Translations</label>
                  {classFormData.translations.map((trans, index) => (
                    <div key={index} style={{ 
                      marginBottom: '1rem', 
                      padding: '1rem', 
                      background: 'white', 
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          Language: 
                          <select
                            value={trans.language}
                            onChange={(e) => handleTranslationChange(index, 'language', e.target.value)}
                            style={{ marginLeft: '0.5rem', padding: '0.25rem', borderRadius: '4px' }}
                          >
                            <option value="en">English</option>
                            <option value="sr">Serbian</option>
                            <option value="es">Spanish</option>
                            <option value="fr">French</option>
                            <option value="de">German</option>
                            <option value="it">Italian</option>
                            <option value="pt">Portuguese</option>
                            <option value="ru">Russian</option>
                            <option value="zh">Chinese</option>
                            <option value="ja">Japanese</option>
                            <option value="ko">Korean</option>
                            <option value="ar">Arabic</option>
                            <option value="hi">Hindi</option>
                          </select>
                        </label>
                        {classFormData.translations.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTranslation(index)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.1)',
                              color: '#ef4444',
                              border: '1px solid #ef4444',
                              borderRadius: '4px',
                              padding: '0.25rem 0.5rem',
                              cursor: 'pointer',
                              fontSize: '0.8rem'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        placeholder="Class name in this language"
                        value={trans.name}
                        onChange={(e) => handleTranslationChange(index, 'name', e.target.value)}
                        required={index === 0}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem', 
                          marginBottom: '0.5rem',
                          fontSize: '0.9rem',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px'
                        }}
                      />
                      <textarea
                        placeholder="Class description (optional)"
                        value={trans.description}
                        onChange={(e) => handleTranslationChange(index, 'description', e.target.value)}
                        rows={2}
                        style={{ 
                          width: '100%', 
                          padding: '0.75rem',
                          fontSize: '0.9rem',
                          border: '1px solid #cbd5e0',
                          borderRadius: '4px',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addTranslation}
                    className="btn-secondary"
                    style={{ alignSelf: 'flex-start', fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                  >
                    + Add Another Language
                  </button>
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={handleCancelEdit}
                    style={{ flex: 1, padding: '0.75rem', fontSize: '0.9rem' }}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn" 
                    style={{ flex: 1, padding: '0.75rem', fontSize: '0.9rem' }}
                  >
                    {editingClassId ? 'Update Class' : 'Create Class'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {loadingClasses ? (
        <div className="loading">Loading classes...</div>
      ) : classes.length > 0 ? (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', 
          gap: '1.5rem' 
        }}>
          {classes.map((classItem) => (
            <div key={classItem.id} style={{
              background: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: classItem.is_system ? '2px solid #667eea' : '1px solid #e2e8f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                {classItem.emoji && <span style={{ fontSize: '2rem' }}>{classItem.emoji}</span>}
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
                    {classItem.name}
                  </h3>
                  {classItem.is_system ? (
                    <span style={{ fontSize: '0.75rem', color: '#667eea', fontWeight: 600 }}>System Class</span>
                  ) : classItem.created_by_user_id && (
                    <span style={{ fontSize: '0.75rem', color: '#667eea', fontWeight: 600 }}>Custom Class</span>
                  )}
                </div>
              </div>
              {classItem.description && (
                <div style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '0.75rem' }}>
                  {classItem.description}
                </div>
              )}
              {!classItem.is_system && (
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  <button
                    className="btn-small"
                    onClick={() => handleEditClass(classItem.id)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-small btn-delete"
                    onClick={() => handleDeleteClass(classItem.id, classItem.is_system)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No classes found. Create your first class to get started.</p>
        </div>
      )}
    </div>
  );
};

export default SchoolClassesTab;

